import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, AlertCircle, Users, Ban, Gavel, Mail, Store, Coins } from 'lucide-react';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      {/* Header */}
      <header className="border-b border-secondary-200 dark:border-secondary-800">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <h1 className="text-xl font-bold text-secondary-900 dark:text-white">Terms & Conditions</h1>
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              Effective Date: August 2026
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="prose prose-sm prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <div className="bg-secondary-100 dark:bg-secondary-900 rounded-lg p-6 mb-8">
              <div className="flex items-start gap-4">
                <FileText className="h-5 w-5 text-secondary-500 mt-1" />
                <div>
                  <h2 className="text-base font-semibold mb-2 text-secondary-900 dark:text-white">Agreement to Terms</h2>
                  <p className="text-secondary-700 dark:text-secondary-300">
                    These Terms and Conditions ("Terms") govern your use of Wisetree AI ("Service") operated by Wisetree AI Pty Ltd ("we", "us", or "our").
                    Wisetree AI is a storytelling studio: an AI production crew helps you create video stories, ad videos, and other media works, which
                    you can keep, export, and — where the marketplace is available — offer for sale to other users. By accessing or using our Service, you
                    agree to be bound by these Terms. If you disagree with any part of these terms, you may not access the Service.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Account Terms */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-secondary-500" />
              1. Account Terms
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <ul className="space-y-3 text-secondary-700 dark:text-secondary-300">
                <li>
                  <strong>1.1 Account Creation:</strong> You must provide accurate, complete, and current information during registration.
                  You are responsible for maintaining the confidentiality of your account credentials.
                </li>
                <li>
                  <strong>1.2 Age Requirements:</strong> You must be at least 13 years old to use this Service. If you are under 18,
                  you must have your parent or legal guardian's permission.
                </li>
                <li>
                  <strong>1.3 Account Responsibility:</strong> You are responsible for all activities that occur under your account,
                  whether or not you know about them.
                </li>
                <li>
                  <strong>1.4 Account Security:</strong> You must immediately notify us of any unauthorized use of your account or
                  any other breach of security.
                </li>
              </ul>
            </div>
          </section>

          {/* Acceptable Use */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-secondary-500" />
              2. Acceptable Use
            </h2>

            <div className="space-y-6">
              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">You agree to use the Service only for lawful purposes and in accordance with these Terms.</h3>

                <div className="mt-4">
                  <p className="text-secondary-700 dark:text-secondary-300 mb-3">You agree NOT to use the Service:</p>
                  <ul className="space-y-2 text-secondary-700 dark:text-secondary-300">
                    <li>• In any way that violates any applicable federal, state, local, or international law or regulation</li>
                    <li>• To transmit any material that is defamatory, offensive, or otherwise objectionable</li>
                    <li>• To create, sell, or distribute content that impersonates a real person, or any intimate, sexual, or non-consensual depiction of any person</li>
                    <li>• To create or sell content that infringes another party's copyright, trademark, likeness, or other rights, or that you do not have the rights to</li>
                    <li>• To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Service</li>
                    <li>• To introduce any viruses, trojan horses, worms, or other material that is malicious or harmful</li>
                    <li>• To attempt to gain unauthorized access to any portion of the Service or any other systems or networks</li>
                    <li>• To harvest or collect email addresses or other contact information without express consent</li>
                    <li>• To manipulate credits, marketplace listings, sales, or payouts — including self-dealing, wash trading, or laundering credits into cash</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* User Content */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-secondary-500" />
              3. User Content
            </h2>

            <div className="space-y-4">
              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">3.1 Your Inputs and Your Works</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  "User Content" means everything you provide to or make with the Service — your prompts, briefs, uploads, edits, and the media works you
                  create (your "Works"). As between you and us, we claim no ownership of your User Content or your Works. You are free to keep, use, export,
                  publish, and — where the marketplace is available — sell your Works, to the fullest extent permitted by law. By using the Service, you
                  grant us a limited, non-exclusive, worldwide license to host, store, process, and display your User Content solely to operate and provide
                  the Service to you (and, if you choose to list a Work, to display it and deliver it to buyers as you direct).
                </p>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 border border-secondary-300 dark:border-secondary-700 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">3.2 Ownership of AI-Generated Output — Please Read</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  Your Works are produced with generative AI. The legal status of purely AI-generated output is still developing and varies by country. In
                  some jurisdictions — including, at present, the United States — material generated solely by AI, without sufficient human authorship, may
                  <strong> not be eligible for copyright protection</strong>. This means you may not be able to register or fully enforce copyright in a
                  Work, and others may be able to use similar or identical AI outputs.
                </p>
                <p className="text-secondary-700 dark:text-secondary-300 mt-3">
                  We do <strong>not</strong> promise, and cannot guarantee, that any Work is protectable by copyright or is free from third-party rights.
                  What we do provide is clear ownership terms between you and us (we do not claim your Works), the ability to keep and export them, and, where
                  available, provenance information for what you make. Your creative direction, selection, arrangement, and edits are the human authorship
                  that may strengthen your rights — but you are responsible for assessing the legal status of each Work for your intended use, and for
                  obtaining professional advice where it matters.
                </p>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">3.3 Content Responsibility</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  You are solely responsible for your User Content and your Works. You represent and warrant that you own or have all necessary rights to
                  your inputs and to create, publish, and (if you list it) sell each Work, and that your Works do not infringe any third party's copyright,
                  trademark, likeness, privacy, or other rights, and do not violate our Acceptable Use terms.
                </p>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">3.4 Content Backup</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  While we perform regular backups of User Content, you are responsible for maintaining your own backup copies of your important data.
                  We are not responsible for any loss or corruption of User Content.
                </p>
              </div>
            </div>
          </section>

          {/* Credits & Payments */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <Coins className="h-5 w-5 text-secondary-500" />
              4. Credits, Plans &amp; Payments
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <ul className="space-y-3 text-secondary-700 dark:text-secondary-300">
                <li>
                  <strong>4.1 Credits:</strong> The Service runs on in-app credits used to power creation (for example, text and media generation).
                  New accounts receive a starting grant, and all users receive a periodic free top-up, subject to a balance cap. Credits are a limited,
                  revocable license to use features of the Service; they have no cash value except where expressly provided by the marketplace and payout
                  terms, and free or granted credits are not redeemable for cash.
                </li>
                <li>
                  <strong>4.2 Charge on success:</strong> Metered creations are charged only when they successfully produce and deliver an output. You are
                  shown the cost before a paid action, and a failed generation is not charged.
                </li>
                <li>
                  <strong>4.3 Plans &amp; top-ups:</strong> Subscriptions renew until cancelled and grant a periodic credit refresh subject to a cap.
                  Purchased top-up credits do not expire and are not subject to the cap. Prices are shown at purchase and may change with reasonable notice.
                </li>
                <li>
                  <strong>4.4 Payments &amp; refunds:</strong> Payments are processed by our third-party payment provider; by purchasing you also agree to
                  their terms. Except where required by law (including the Australian Consumer Law) or expressly stated, purchases of credits and
                  subscription fees are non-refundable. You may cancel a subscription at any time; cancellation stops future renewals and takes effect at the
                  end of the current billing period.
                </li>
              </ul>
            </div>
          </section>

          {/* Marketplace & Sales */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <Store className="h-5 w-5 text-secondary-500" />
              5. Marketplace &amp; Selling Your Works
            </h2>
            <p className="text-sm text-secondary-500 mb-3">
              The marketplace and payouts are rolling out in phases; some features described below may not yet be available in your account. These terms
              apply whenever they are.
            </p>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <ul className="space-y-3 text-secondary-700 dark:text-secondary-300">
                <li>
                  <strong>5.1 Listing:</strong> If the marketplace is available to you, you may list eligible Works for sale, priced in credits. You set the
                  price within any limits we specify. By listing, you represent and warrant that you have all rights necessary to sell the Work and that it
                  complies with these Terms and our Acceptable Use terms, and you grant buyers the license described in your listing and grant us the rights
                  needed to display, preview, and deliver the Work.
                </li>
                <li>
                  <strong>5.2 Platform fee:</strong> When a Work sells, we retain a platform fee of <strong>20% of the sale</strong> (comprising a 10%
                  copyright/licensing royalty and a 10% marketplace commission), and the remaining <strong>80% accrues to you</strong> as seller earnings.
                  We may adjust the fee with reasonable notice; the fee in effect at the time of a sale applies to that sale.
                </li>
                <li>
                  <strong>5.3 Earnings &amp; payouts:</strong> Seller earnings accrue as a balance and become payable to your nominated account through our
                  payments partner, subject to a minimum payout threshold, identity and tax verification, holding periods, and anti-fraud review. Earnings
                  may be withheld or reversed for refunds, chargebacks, disputed or infringing listings, or suspected abuse.
                </li>
                <li>
                  <strong>5.4 Buyer license:</strong> When you buy a Work, you receive the license described in its listing for the credits you spend. Given
                  Section 3.2, a purchase conveys the seller's and our rights in the delivered files and the stated usage license — it is not a warranty that
                  the Work is protected by copyright or exclusive to you.
                </li>
                <li>
                  <strong>5.5 Enforcement:</strong> We may remove listings, reverse sales, withhold earnings, and suspend marketplace access for content or
                  conduct that violates these Terms, and we operate a rights-holder reporting and takedown process.
                </li>
              </ul>
            </div>
          </section>

          {/* Service and Pricing */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-secondary-500" />
              6. Service Availability and Modifications
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <ul className="space-y-3 text-secondary-700 dark:text-secondary-300">
                <li>
                  <strong>6.1 Service Availability:</strong> We strive to provide continuous availability of the Service, but we do not guarantee
                  uninterrupted access. We may experience hardware, software, or other problems requiring maintenance.
                </li>
                <li>
                  <strong>6.2 Service Modifications:</strong> We reserve the right to modify or discontinue, temporarily or permanently,
                  the Service (or any part thereof) with or without notice.
                </li>
                <li>
                  <strong>6.3 Pricing Changes:</strong> We reserve the right to change our pricing at any time. Any pricing changes will be
                  communicated to you with reasonable advance notice.
                </li>
                <li>
                  <strong>6.4 Feature Updates:</strong> We may add, modify, or remove features from the Service at our discretion to improve
                  functionality and user experience.
                </li>
              </ul>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">7. Intellectual Property Rights</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                The Service and its original content (excluding User Content), features, and functionality are and will remain the exclusive
                property of Wisetree AI Pty Ltd and its licensors. The Service is protected by copyright, trademark, and other laws.
                Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
              </p>
              <div className="mt-4 p-4 bg-secondary-200 dark:bg-secondary-800 rounded-lg">
                <p className="text-secondary-500 font-semibold">
                  You acknowledge that Wisetree AI Pty Ltd owns all rights, title, and interest in and to the Service, including all intellectual property rights.
                </p>
              </div>
            </div>
          </section>

          {/* Privacy */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">8. Privacy</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                Your use of the Service is also governed by our Privacy Policy. Please review our{' '}
                <Link to="/privacy" className="text-secondary-700 dark:text-secondary-300 hover:text-secondary-900 dark:hover:text-white underline">
                  Privacy Policy
                </Link>
                , which explains how we collect, use, and disclose information about you when you access or use our Service.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <Ban className="h-5 w-5 text-secondary-500" />
              9. Termination
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <ul className="space-y-3 text-secondary-700 dark:text-secondary-300">
                <li>
                  <strong>9.1 Termination by You:</strong> You may terminate your account at any time by contacting us or through the account settings.
                </li>
                <li>
                  <strong>9.2 Termination by Us:</strong> We may terminate or suspend your account immediately, without prior notice or liability,
                  for any reason, including breach of these Terms.
                </li>
                <li>
                  <strong>9.3 Effect of Termination:</strong> Upon termination, your right to use the Service will cease immediately.
                  We may delete your User Content after a reasonable period following account termination. Any seller earnings validly accrued and not
                  subject to reversal remain payable, subject to the marketplace and payout terms.
                </li>
                <li>
                  <strong>9.4 Survival:</strong> Provisions that by their nature should survive termination shall survive, including ownership provisions,
                  warranty disclaimers, indemnity, and limitations of liability.
                </li>
              </ul>
            </div>
          </section>

          {/* Disclaimers */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">10. Disclaimers — AI Outputs, No Professional Advice & Assumption of Risk</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 border border-secondary-300 dark:border-secondary-700 rounded-lg p-6 space-y-4">
              <div>
                <h3 className="text-base font-semibold mb-2 text-secondary-900 dark:text-white">10.1 AI-Generated Content</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  The Service uses artificial intelligence and large language models to generate suggestions, plans, analyses, charts, drafts, code,
                  and other outputs ("AI Outputs"). AI Outputs are produced automatically and may be inaccurate, incomplete, outdated, biased,
                  fabricated ("hallucinated"), or otherwise unreliable. AI Outputs are provided for general informational purposes only.
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold mb-2 text-secondary-900 dark:text-white">10.2 No Professional Advice</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  AI Outputs and the Service do <strong>not</strong> constitute professional advice of any kind — including legal, financial,
                  investment, tax, accounting, medical, engineering, or business advice — and must not be relied upon as a substitute for
                  consultation with an appropriately qualified professional. No fiduciary, advisory, or professional-client relationship is created
                  by your use of the Service.
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold mb-2 text-secondary-900 dark:text-white">10.3 Your Responsibility &amp; Assumption of Risk</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  You are solely responsible for independently reviewing, verifying, and validating any AI Output before relying on it, and for all
                  decisions you make and actions you take based on the Service. <strong>Your use of the Service and any reliance on AI Outputs is
                  entirely at your own risk, and you assume full responsibility for the outcomes.</strong>
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold mb-2 text-secondary-900 dark:text-white">10.4 "As Is" / "As Available"</h3>
                <p className="text-secondary-700 dark:text-secondary-300 uppercase font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
                  EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                  NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
                </p>
                <p className="text-secondary-700 dark:text-secondary-300 mt-3">
                  We do not warrant that the Service will be uninterrupted, timely, secure, or error-free, or that any results or AI Outputs obtained
                  from the Service will be accurate, complete, or reliable.
                </p>
              </div>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">11. Limitation of Liability</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 border border-secondary-300 dark:border-secondary-700 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300 uppercase font-semibold">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WISETREE AI PTY LTD, ITS AFFILIATES, DIRECTORS, EMPLOYEES, CONTRACTORS, OR
                AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
                DATA, USE, GOODWILL, BUSINESS, OR OPPORTUNITY, ARISING OUT OF OR IN CONNECTION WITH THE SERVICE OR THESE TERMS, WHETHER IN CONTRACT,
                TORT (INCLUDING NEGLIGENCE), OR OTHERWISE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
              <p className="text-secondary-700 dark:text-secondary-300 mt-4">
                Without limiting the above, we are not liable for any loss or damage arising from or relating to: (a) your reliance on, or any decision,
                action, or omission you make based on, any AI Output; (b) errors, inaccuracies, omissions, or "hallucinations" in any AI Output;
                (c) loss, corruption, or unauthorized access to data, or any data breach or cybersecurity incident beyond our reasonable control; or
                (d) the acts, omissions, products, or services of third parties (including Google, cloud-hosting providers, and AI-model providers).
              </p>
              <p className="text-secondary-700 dark:text-secondary-300 mt-4 font-semibold">
                Where any liability cannot lawfully be excluded, our total aggregate liability to you for all claims arising from or related to these
                Terms or the Service is limited to the greater of (a) the total amount you paid us in the twelve (12) months preceding the event giving
                rise to the claim, or (b) AUD&nbsp;$100.
              </p>
              <p className="text-secondary-700 dark:text-secondary-300 mt-4">
                <strong>Australian Consumer Law.</strong> Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or
                remedy you may have under the Australian Consumer Law or any other law that cannot lawfully be excluded. To the extent we are able to
                limit our liability for a failure to comply with a non-excludable guarantee, our liability is limited, at our option, to resupplying the
                relevant service or paying the cost of having it resupplied.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">12. Indemnification</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                You agree to defend, indemnify, and hold harmless Wisetree AI Pty Ltd and its affiliates, licensors, and service providers, and its and
                their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any
                claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable legal fees) arising out of or
                relating to: (a) your violation of these Terms; (b) your use of the Service; (c) your User Content or Works, including any Work you list,
                sell, or buy on the marketplace and any claim that it infringes a third party's rights; or (d) any decision, action, or omission you make in
                reliance on the Service or any AI Output.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <Gavel className="h-5 w-5 text-secondary-500" />
              13. Governing Law and Jurisdiction
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                These Terms shall be governed and construed in accordance with the laws of Australia, without regard to its conflict of
                law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                Any legal action or proceeding arising under these Terms will be brought exclusively in the courts of Australia.
              </p>
            </div>
          </section>

          {/* General */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">14. General Provisions</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <ul className="space-y-3 text-secondary-700 dark:text-secondary-300">
                <li>
                  <strong>14.1 Entire Agreement:</strong> These Terms constitute the entire agreement between you and Wisetree AI Pty Ltd regarding the Service
                  and supersede all prior agreements and understandings.
                </li>
                <li>
                  <strong>14.2 Severability:</strong> If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions
                  will continue in full force and effect.
                </li>
                <li>
                  <strong>14.3 Waiver:</strong> No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any
                  other term.
                </li>
                <li>
                  <strong>14.4 Assignment:</strong> You may not assign or transfer these Terms or your rights under these Terms without our prior written
                  consent. We may assign our rights under these Terms without restriction.
                </li>
                <li>
                  <strong>14.5 Force Majeure:</strong> We shall not be liable for any failure or delay in performance due to circumstances beyond our
                  reasonable control.
                </li>
              </ul>
            </div>
          </section>

          {/* Changes to Terms */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4">15. Changes to Terms</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by email or by posting a notice
                on the Service prior to the effective date of the changes. By continuing to use the Service after the effective date, you agree to be
                bound by the modified Terms.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5 text-secondary-500" />
              16. Contact Information
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="space-y-2 text-secondary-700 dark:text-secondary-300">
                <p><strong>Wisetree AI Pty Ltd</strong></p>
                <p><strong>Support:</strong> support@wisetree.ai</p>
                <p className="mt-4 text-sm text-secondary-500">
                  <strong>SOC 2 Compliant Infrastructure:</strong> Wisetree AI is built on Google Cloud Platform with SOC 2 Type II certification,
                  providing enterprise-grade security and reliability.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-secondary-200 dark:border-secondary-800 mt-20">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              © 2026 Wisetree AI Pty Ltd. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/privacy" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/dashboard" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;